<template>
    <section>
        <template v-if="filteredItems.length === 0">
            <div v-if="isLoading && !query" class="user-nfts">
                <template v-for="i in limit">
                    <div v-bind:key="`tab_user_nft_skeleton_${i}`" class="user-nft user-nft--skeleton">
                        <div class="user-nft__image">
                            <div class="skeleton"/>
                        </div>
                        <footer class="user-nft__footer">
                            <h4 class="user-nft__collection">
                                <span class="user-nft__collection__text skeleton">NFT collection name</span>
                            </h4>
                            <h3 class="user-nft__name">
                                <span class="skeleton">NFT name</span>
                            </h3>
                        </footer>
                    </div>
                </template>
            </div>

            <!-- Searching: the sentinel below keeps pulling pages, its spinner is the progress.
                 Once it gives up mid-list, the count says how far it got and its button goes on. -->
            <div v-else-if="query && !isLoading && !hasMore" class="tx-history-empty-panel">
                {{ $t('address.nfts.search_empty') }}
            </div>

            <div v-else-if="query && !isLoading && searchStalled" class="tx-history-empty-panel">
                {{ $tc('address.nfts.search_empty_so_far', items.length) }}
            </div>

            <div v-else-if="!query" class="tx-history-empty-panel">
                {{ $t('address.nfts.no_nfts') }}
            </div>
        </template>

        <template v-else>
            <section class="user-nfts">
                <tab-user-nft-item
                    v-for="item in filteredItems"
                    v-bind:address="addressBook[item.address].user_friendly"
                    v-bind:content="metadata[item.address].token_info[0]"
                    v-bind:collection="metadata[item?.collection_address]?.token_info[0]"
                    v-bind:collection-address="addressBook[item?.collection_address]?.user_friendly || null"
                    v-bind:key="`nft_item_${item.address}`"
                />
            </section>
        </template>

        <ui-mugen-scroll
            v-if="filteredItems.length !== 0 || query"
            ref="scroll"
            style="margin-bottom: 6px;"
            v-bind:handler="loadData"
            v-bind:shouldHandle="shouldHandleScroll"
            v-bind:showButton="showPreloader"
            v-bind:isLoading="isLoading"/>
    </section>
</template>

<script>
import { getNFTsByOwnerAddress } from '~/api/toncenterV3';
import { isWhitelistedCollection } from '~/nftCollectionWhitelist.js';
import TabUserNftItem from './TabUserNftItem.vue';

// Page size while skipping over hidden spam or looking for a search match, see loadData():
const HIDDEN_PAGE_LIMIT = 100;

// A search that matches nothing would otherwise walk the whole wallet in one go.
// After this many silent pages the loader stops and offers a button instead:
const SEARCH_PAGES_PER_PASS = 5;

export default {
    props: {
        address: String,
    },

    data() {
        return {
            items: [],
            addressBook: {},
            metadata: {},
            isLoading: true,
            hasMore: true,
            limit: 25,
            offset: 0,
            searchStalled: false,
        };
    },

    computed: {
        // Everything outside of the whitelist is spam more often than not, so
        // it's hidden until the eye in the tab header is clicked.
        visibleItems() {
            return this.$store.state.nftSpamShown
                ? this.items
                : this.items.filter(item => this.isWhitelisted(item));
        },

        // Typed into the loupe in the tab header, see NftSearch.vue:
        query() {
            return this.$store.state.nftSearchQuery;
        },

        // Search runs over what's been loaded so far, the way acton does it:
        // the indexer has no name search for a wallet's items, and the loader
        // below keeps fetching pages while nothing on the grid matches.
        filteredItems() {
            const query = this.query.toLowerCase();

            if (!query) {
                return this.visibleItems;
            }

            return this.visibleItems.filter(item => this.searchText(item).includes(query));
        },

        // The toggle lives in the tab header, on the other side of the tab
        // component, so the number it prints goes through the store. It only
        // counts what's been loaded so far, growing as the user scrolls.
        spamCount() {
            return this.items.filter(item => !this.isWhitelisted(item)).length;
        },

        // Pagination follows what's on the screen: when everything loaded so
        // far is hidden spam, there's nothing to scroll past, and the loader
        // would otherwise stay in view and fetch page after page by itself.
        // With a search on, the sentinel stays even over an empty grid: that
        // is exactly when the next pages are worth pulling.
        shouldHandleScroll() {
            return !this.isLoading
                && this.address
                && this.hasMore
                && (this.filteredItems.length > 0 || this.query !== '');
        },

        showPreloader() {
            return this.address
                && (this.filteredItems.length > 0 || this.query !== '')
                && this.hasMore;
        },
    },

    watch: {
        spamCount: {
            immediate: true,
            handler(count) {
                this.$store.commit('updateNftSpamCount', count);
            },
        },

        // The grid shrinks under a new query and the sentinel rises into view
        // without any scrolling, so the observer never hears about it -- ask
        // the sentinel by hand once the shorter grid is on screen.
        query() {
            this.searchStalled = false;
            this.$nextTick(() => {
                this.$refs.scroll?.maybeHandle();
            });
        },
    },

    mounted() {
        if (this.isMobile || this.isTablet) this.limit = 15;
        this.loadData();
    },

    beforeDestroy() {
        this.$store.commit('updateNftSpamCount', 0);
    },

    methods: {
        isWhitelisted(item) {
            return isWhitelistedCollection(this.addressBook[item?.collection_address]?.user_friendly);
        },

        // Everything a visitor may type to find a card: its name (domains and
        // numbers keep theirs in extra), collection name, both addresses and
        // the index inside the collection.
        searchText(item) {
            const content = this.metadata[item.address]?.token_info?.[0];
            const collection = this.metadata[item.collection_address]?.token_info?.[0];

            return [
                content?.name,
                content?.domain,
                content?.extra?.domain,
                collection?.name,
                this.addressBook[item.address]?.user_friendly,
                this.addressBook[item.collection_address]?.user_friendly,
                item.index,
            ].filter(Boolean).join(' ').toLowerCase();
        },

        async loadData() {
            this.isLoading = true;

            try {
                const visibleBefore = this.filteredItems.length;
                let limit = this.limit;
                let pages = 0;

                // A page that is spam from top to bottom (or matches nothing in
                // the search) adds nothing to the grid, so the scroll sentinel
                // would take it for a failed request and put the "load more"
                // button back. Keep pulling until something shows up or the
                // list ends -- in bigger pages, since hidden cards are never
                // rendered and cost nothing beyond the size of the response.
                do {
                    // eslint-disable-next-line no-await-in-loop
                    const nfts = await getNFTsByOwnerAddress(this.address, limit, this.offset);
                    const items = nfts?.nft_items || [];
                    this.items = this.items.concat(items);
                    this.addressBook = Object.assign(this.addressBook, nfts?.address_book);
                    this.metadata = Object.assign(this.metadata, nfts?.metadata);
                    this.hasMore = items.length >= limit;
                    this.offset += items.length;
                    limit = HIDDEN_PAGE_LIMIT;
                    pages += 1;
                } while (this.hasMore
                    && this.filteredItems.length === visibleBefore
                    && (!this.query || pages < SEARCH_PAGES_PER_PASS));

                this.searchStalled = this.query !== ''
                    && this.hasMore
                    && this.filteredItems.length === visibleBefore;
            } catch (error) {
                console.error('Error loading nfts:', error);
            } finally {
                this.isLoading = false;
            }
        },
    },

    components: {
        TabUserNftItem,
    },
};
</script>

<style lang="scss">
.user-nfts {
    padding: 24px;
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: repeat(5, 1fr);
    grid-column-gap: 20px;
    grid-row-gap: 20px;
    @media all and (max-width: 1200px) {
        & {
            padding: 18px;
            grid-template-columns: repeat(4, 1fr);
            grid-column-gap: 18px;
            grid-row-gap: 18px;
        }
    }
    @media all and (max-width: 900px) {
        & { grid-template-columns: repeat(3, 1fr); }
    }
    @media all and (max-width: 600px) {
        & { grid-template-columns: repeat(2, 1fr); }
    }
    @media all and (max-width: 480px) {
        & {
            padding: 14px;
            grid-column-gap: 14px;
            grid-row-gap: 14px;
        }
    }
}

.user-nft {
    position: relative;
    color: inherit;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    background-color: var(--user-nft-list-item-background);
    box-shadow: 1px 1px var(--user-nft-list-item-box-shadow-blur) rgba(0, 0, 0, 0.19);
    border-radius: 8px;
    overflow: hidden;
    transition: .2s background ease;
    &:hover {
        text-decoration: none;
    }
    @media (hover: hover) {
        &:hover {
            background-color: var(--user-nft-list-item-hover-background);
            .user-nft__image__img {
                transform: scale(1.08);
            }
        }
    }
    &--skeleton {
        pointer-events: none;
        user-select: none;
    }
    &__image {
        font-size: 0;
        width: 100%;
        overflow: hidden;
        display: block;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        aspect-ratio: 1 / 1;
        &__img {
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
            width: 100%;
            height: 100%;
            object-fit: cover;
            transform: none;
            transition: .2s transform ease;
        }
        &__broken {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            background-color: var(--user-nft-list-item-broken-background);
            color: var(--user-nft-list-item-broken-icon-color);
            svg {
                width: 87px;
                height: 87px;
                max-width: 50%;
                max-height: 50%;
            }
        }
        .skeleton {
            border-radius: 0;
            background-image: none;
            box-shadow: none;
            aspect-ratio: 1 / 1;
            width: 100%;
        }
    }
    &__footer {
        padding: 12px 12px 10px;
    }
    &__name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0;
        font-weight: 500;
        font-size: 16px;
    }
    &__collection {
        padding: 4px 4px 8px 4px;
        margin: -4px;
        font-weight: normal;
        font-size: 13px;
        color: var(--user-nft-list-item-text-muted-color);
        display: flex;
        align-items: center;
        white-space: nowrap;
        &__text {
            overflow: hidden;
            text-overflow: ellipsis;
        }
        &__icon {
            flex-shrink: 0;
            width: 1em;
            height: 1em;
            margin-left: 0.3em;
            fill: var(--blue-bright);
        }
    }
    &__btn {
        display: block;
        width: auto;
        max-width: 180px;
        line-height: 40px;
        padding: 0 15px;
        margin: 20px auto 0 auto;
        background: var(--big-blue-button-background);
        color: #FFF;
        font-weight: 500;
        font-size: 14px;
        border-radius: 6px;
        text-align: center;
        cursor: pointer;
        -webkit-transition: all .15s;
        -o-transition: all .15s;
        transition: all .15s;
    }

    &__btn:hover {
        text-decoration: none;
        background: var(--big-blue-button-hover-background);
    }
}

@media all and (max-width: 480px) {
    .user-nft {
        &__footer {
            padding: 10px;
        }
        &__name {
            font-size: 14px;
        }
        &__collection {
            font-size: 11px;
        }
    }
}
</style>
